/**
 * ReviewHub step 2 (ADR-0023 "Step 2 design", mockup outputs/reviewhub-evidence-mockup-20261029.html):
 * the Evidence tab (checks, sources, uploads), the Tech Prep tab, the scorecard's evidence card and
 * the source line under each measure. Uploads are read here (read-excel-file, pdf.js) and parsed by
 * the API with the same shared parsers; the original file is kept with the review.
 */
import { useState } from "react";
import { api, type RhEvidence, type RhFound, type RhMetricDef, type RhReview } from "../../lib/api";
import { useStore } from "../../lib/store";
import { fmt, fmtDT } from "../../lib/format";
import { MEASURE_SOURCE, SOURCE_LABEL, UPLOAD_LABEL, type SourceKind, type UploadKind } from "../../../../shared/evidence";
import { todayEastern } from "../../../../shared/agreements";

type Run = (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;
const AUTO: SourceKind[] = ["atera", "m365", "dns", "techhub", "review"];
const GROUPS = [
  { key: "auto", label: "Pulled automatically", cls: "ev-auto", color: "#17786C", has: (s: SourceKind) => AUTO.includes(s) },
  { key: "upload", label: "From uploads", cls: "ev-upl", color: "#1F5FA8", has: (s: SourceKind) => s === "upload" },
  { key: "techprep", label: "Tech Prep", cls: "ev-tp", color: "#4A5462", has: (s: SourceKind) => s === "techprep" },
  { key: "owner", label: "Owner", cls: "ev-own", color: "#C08A12", has: (s: SourceKind) => s === "owner" },
] as const;

/** The small colored label under a measure: where its number came from. */
export function SourceChip({ found, typed, note, metric }: { found: RhFound | null; typed: unknown; note: string; metric: string }) {
  const planned = MEASURE_SOURCE[metric]?.source;
  const same = found && typed !== null && typed !== undefined && String(typed) === String(found.value);
  if (found && (typed === null || typed === undefined || same)) return <span className={`ev-src ${found.source === "upload" ? "ev-upl" : "ev-auto"}`} title={found.detail}>{SOURCE_LABEL[found.source as SourceKind] ?? found.source}</span>;
  if (found && typed !== null && typed !== undefined) return <span className="ev-src ev-hand" title={`${SOURCE_LABEL[found.source as SourceKind] ?? found.source} found ${String(found.value)}. ${note}`}>Changed{note ? `: ${note}` : ""}</span>;
  if (planned === "techprep") return <span className="ev-src ev-tp">Tech Prep</span>;
  if (planned === "owner") return <span className="ev-src ev-own">Owner</span>;
  return null;
}

export function EvidenceSummary({ data, ev, onOpen, onPull }: { data: RhReview; ev: RhEvidence | null; onOpen: () => void; onPull: () => void }) {
  const counts = GROUPS.map((g) => ({ ...g, n: Object.entries(MEASURE_SOURCE).filter(([, s]) => g.has(s.source)).length }));
  const total = counts.reduce((s, c) => s + c.n, 0);
  const st = { pass: 0, flag: 0, fail: 0 };
  for (const c of ev?.checks ?? []) if (c.status in st) st[c.status as keyof typeof st]++;
  const filled = data.metrics.filter((m) => m.value !== null).length;
  return (
    <div className="card sect ev-sum">
      <h3>Evidence for this review</h3>
      <div className="cap">{ev?.pulled_at ? `Pulled ${fmtDT(ev.pulled_at)}. Pulls again every morning until the meeting.` : "Not pulled yet. The first pull runs tomorrow morning, or now."}</div>
      <div className="ev-bar">{counts.map((c) => <span key={c.key} style={{ width: `${(100 * c.n) / total}%`, background: c.color }} />)}</div>
      <div className="ev-leg">{counts.map((c) => [<span key={c.key}><span className={`ev-src ${c.cls}`}>{c.label}</span></span>, <b key={`${c.key}-n`}>{c.n}</b>])}</div>
      <div style={{ fontSize: 13.5 }}><b>{filled}</b> of {data.metrics.length} measures have a figure</div>
      {ev && ev.checks.length > 0 && <div style={{ fontSize: 13.5 }}><b>Checks:</b> {st.pass} pass, {st.flag} flag{st.flag === 1 ? "" : "s"}, {st.fail} fail{st.fail === 1 ? "" : "s"}</div>}
      <div className="row-btns" style={{ gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onOpen}>Open Evidence</button>
        {data.permissions.edit && <button className="btn btn-ghost btn-sm" onClick={onPull}>Pull now</button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- reading files in the browser
async function base64Of(f: File): Promise<string> {
  const buf = new Uint8Array(await f.arrayBuffer());
  let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}
async function sheetRows(f: File, sheet: string): Promise<unknown[][]> {
  const { readSheet } = await import("read-excel-file/browser");
  try { return (await readSheet(f, sheet)) as unknown[][]; } catch { return (await readSheet(f)) as unknown[][]; }
}
/** PDF text in reading order, one line per row of text (close to pypdf's layout mode). */
async function pdfText(f: File): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  const doc = await pdfjs.getDocument({ data: await f.arrayBuffer() }).promise;
  const out: string[] = [];
  for (let p = 1; p <= Math.min(doc.numPages, 40); p++) {
    const items = (await (await doc.getPage(p)).getTextContent()).items as { str: string; transform: number[] }[];
    const lines = new Map<number, { x: number; s: string }[]>();
    for (const it of items) { const y = Math.round(it.transform[5]! / 3); (lines.get(y) ?? lines.set(y, []).get(y)!).push({ x: it.transform[4]!, s: it.str }); }
    for (const y of [...lines.keys()].sort((a, b) => b - a)) out.push(lines.get(y)!.sort((a, b) => a.x - b.x).map((b) => b.s).join(" "));
  }
  return out.join("\n");
}
async function readUpload(kind: UploadKind, f: File) {
  const body: { kind: string; file_name: string; content_base64: string; rows?: unknown[][]; text?: string } = { kind, file_name: f.name, content_base64: await base64Of(f) };
  if (kind === "sla") body.rows = await sheetRows(f, "Summary");
  else if (kind === "auditor") body.rows = await sheetRows(f, "Auditor");
  else if (kind === "learning" || kind === "phishing") { if (!/\.pdf$/i.test(f.name)) throw new Error("Huntress reports are PDFs."); body.text = await pdfText(f); }
  return body;
}
const guessKind = (name: string): UploadKind | null => (/\.pdf$/i.test(name) ? "learning" : /sla/i.test(name) ? "sla" : /auditor/i.test(name) ? "auditor" : null);

// ---------------------------------------------------------------- Evidence tab
const STATUS_WORD = { pass: "Pass", flag: "Flag", fail: "Fail", later: "Later" } as const;
const SLOTS: { kind: UploadKind; help: string; accept: string }[] = [
  { kind: "sla", help: "Excel · run it with the quarter's dates; the dates in the file name are checked", accept: ".xlsx" },
  { kind: "auditor", help: "Excel · this client only", accept: ".xlsx" },
  { kind: "learning", help: "PDF, one a month", accept: ".pdf" },
  { kind: "phishing", help: "PDF", accept: ".pdf" },
  { kind: "backup", help: "Optional · any format, kept with the review", accept: "" },
];
export function EvidenceTab({ data, ev, setEv, run }: { data: RhReview; ev: RhEvidence | null; setEv: (e: RhEvidence) => void; run: Run }) {
  const { toast } = useStore();
  const [busy, setBusy] = useState<string | null>(null);
  const edit = data.permissions.edit;
  const id = data.review.id;
  const upload = async (kind: UploadKind, f: File | undefined) => {
    if (!f) return;
    setBusy(kind);
    try {
      const out = await api.rhUpload(id, await readUpload(kind, f));
      setEv(out.evidence);
      await run(async () => undefined, `${out.label}: ${out.summary || "kept with the review"}.`);
    } catch (e) { toast((e as Error).message); }
    setBusy(null);
  };
  const pull = async () => { setBusy("pull"); try { setEv(await api.rhPull(id)); await run(async () => undefined, "Pulled. The scorecard is up to date."); } catch (e) { toast((e as Error).message); } setBusy(null); };
  if (!ev) return <div className="card empty">Loading the evidence…</div>;
  return (
    <div className="ev-grid">
      <div className="card ev-card">
        <h3>Checks</h3>
        <div className="cap">The checks from the TBR collector, run on every pull. A flag needs a look; a fail has to be fixed or explained before the meeting.</div>
        {ev.checks.length ? ev.checks.map((c) => (
          <div key={c.key} className="ev-row"><span className={`ev-st ${c.status}`}>{STATUS_WORD[c.status]}</span><div><b>{c.title}</b><small>{c.detail}</small></div></div>
        )) : <div className="cap" style={{ marginTop: 10 }}>No pull yet.</div>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="card ev-card">
          <div className="ev-srch"><h3>Sources</h3>{edit && <button className={`btn btn-ghost btn-sm ${busy === "pull" ? "busy" : ""}`} disabled={!!busy} onClick={() => void pull()}>Pull now</button>}</div>
          <div className="cap">Read only. {ev.pulled_at ? `Last pulled ${fmtDT(ev.pulled_at)}.` : "Not pulled yet."}</div>
          {ev.sources.map((s) => {
            const keys = Object.entries(ev.found).filter(([, f]) => (s.source === "techhub" ? ["techhub", "review"].includes(f.source) : f.source === s.source));
            return (
              <div key={s.source} className="ev-srcrow">
                <div className="ev-srch"><b>{s.label}</b><span className={`cap ev-state-${s.state}`}>{s.state === "ok" ? "Connected" : s.state === "needs" ? "Needs approval" : s.state === "off" ? "Not set up" : "Didn't answer"}</span></div>
                <div className="cap" style={{ margin: "2px 0 0" }}>{s.detail}</div>
                {keys.length > 0 && <div className="ev-gives">{keys.map(([k, f]) => <span key={k} className="ev-g" title={f.detail}>{data.metric_defs.find((d) => d.key === k)?.label ?? k} <i>{show(data.metric_defs.find((d) => d.key === k), f.value)}</i></span>)}</div>}
              </div>
            );
          })}
        </div>
        <div className="card ev-card">
          <h3>Uploads</h3>
          <div className="cap">Reports with no API. DocVault reads the number out of each one and keeps the file with the review.</div>
          {SLOTS.map((sl) => {
            const mine = ev.uploads.filter((u) => u.kind === sl.kind);
            return (
              <div key={sl.kind} className="ev-slot">
                <b>{UPLOAD_LABEL[sl.kind]}</b>
                {edit ? <label className={`btn btn-ghost btn-sm ${busy === sl.kind ? "busy" : ""}`}>Upload<input type="file" accept={sl.accept} hidden onChange={(e) => { void upload(sl.kind, e.target.files?.[0]); e.target.value = ""; }} /></label> : <span />}
                <small>{sl.help}</small>
                {mine.map((u) => (
                  <div key={u.id} className="ev-up-row">
                    <span className="ev-ok">{u.summary || u.file_name}</span>
                    <span className="cap">{u.file_name} · {u.by ?? ""}{u.uploaded_at ? `, ${fmt(u.uploaded_at)}` : ""}</span>
                    <span className="ev-up-acts">
                      <button className="linkbtn" onClick={() => void api.rhUploadUrl(u.id).then((r) => window.open(r.url, "_blank"), (e: Error) => toast(e.message))}>Download</button>
                      {edit && <button className="linkbtn" onClick={() => { if (window.confirm(`Remove ${u.file_name}? The figures it gave are taken out.`)) void run(async () => { await api.rhRemoveUpload(u.id); setEv(await api.rhEvidence(id)); }, "Removed."); }}>Remove</button>}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
          {edit && <label className="ev-drop" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; const k = f && guessKind(f.name); if (!f) return; if (!k) { toast("Use the Upload button on the right report; DocVault couldn't tell which report this is."); return; } void upload(k, f); }}>
            Drop a report here, or use Upload on its line
          </label>}
        </div>
      </div>
    </div>
  );
}
function show(d: RhMetricDef | undefined, v: unknown): string {
  if (v === null || v === undefined) return "-";
  if (!d) return String(v);
  if (d.kind === "bool") return v ? d.yes ?? "Yes" : d.no ?? "No";
  if (d.kind === "enum") return d.options?.find((o) => o.value === v)?.label ?? String(v);
  return `${v}${d.kind === "pct" ? "%" : d.kind === "days" ? " days" : d.kind === "months" ? " months" : ""}`;
}

// ---------------------------------------------------------------- Tech Prep tab
const TP_GROUPS: { title: string; until?: string; keys: string[]; help?: string }[] = [
  { title: "Service", until: "Until Atera is confirmed", keys: ["csat", "recurring_open", "recurring_over_90"] },
  { title: "Backup and recovery", keys: ["critical_unprotected", "offsite_copy", "immutable_copy", "restore_test_days", "last_test_failed", "rto_rpo_met"], help: "From the backup console and the restore test log." },
  { title: "Security tools", until: "Until step 4", keys: ["edr_pct", "open_incidents"], help: "Moves to automatic pulls when Huntress, Sophos and ThreatLocker are connected." },
  { title: "Vulnerabilities", until: "Until step 3", keys: ["crit_vuln_30_90", "crit_vuln_over_90"] },
  { title: "Microsoft 365", keys: ["data_governance"] },
];
/** The compliance answers until step 3 (the budget forecast is the owner's too, typed on the scorecard). */
const OWNER_KEYS = Object.entries(MEASURE_SOURCE).filter(([, s]) => s.source === "owner" && s.until).map(([k]) => k);
const M365_FALLBACK = ["mfa_pct", "secure_score", "legacy_auth"];
export function TechPrepTab({ data, ev, run }: { data: RhReview; ev: RhEvidence | null; run: Run }) {
  const cur = new Map(data.metrics.map((m) => [m.key, m]));
  const def = (k: string) => data.metric_defs.find((d) => d.key === k)!;
  const fallback = M365_FALLBACK.filter((k) => !ev?.found[k]);
  const groups = [...TP_GROUPS, ...(fallback.length ? [{ title: "Microsoft 365 (until the client approves the updated permissions)", keys: fallback }] : []),
    { title: "For the owner", until: "Until step 3", keys: OWNER_KEYS, help: "Compliance answers, until assessments arrive." }];
  const all = groups.flatMap((g) => g.keys);
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries(all.map((k) => [k, toIn(def(k), cur.get(k)?.typed)])));
  const [restoreOn, setRestoreOn] = useState("");
  const answered = all.filter((k) => vals[k] !== "").length;
  const edit = data.permissions.edit;
  const save = () => {
    const values = Object.fromEntries(all.map((k) => { const d = def(k); const v = vals[k]; return [k, v === "" ? null : d.kind === "bool" ? v === "yes" : v]; }));
    void run(() => api.rhMetrics(data.review.id, values, "Tech Prep"), "Saved. The scorecard updated.");
  };
  return (
    <div>
      <div className="card ev-card" style={{ marginBottom: 14 }}>
        <div className="ev-srch"><div><h3>Tech Prep{data.review.engineer_name ? ` for ${data.review.engineer_name}` : ""}</h3>
          <div className="cap" style={{ margin: 0 }}>Only what no system can tell us. Due three days before the meeting; it's in the engineer's My tasks.</div></div>
          {edit && <button className="btn btn-accent" onClick={save}>Save</button>}</div>
        <div className="tp-prog" style={{ marginTop: 12 }}><span>{answered} of {all.length} answered</span><span className="bar"><span style={{ width: `${(100 * answered) / all.length}%` }} /></span></div>
      </div>
      {groups.map((g) => (
        <div key={g.title} className="card tp-sec" style={{ marginBottom: 14 }}>
          <h3>{g.title}{g.until && <span className="ev-src ev-hand">{g.until}</span>}</h3>
          {g.help && <div className="cap">{g.help}</div>}
          {g.keys.map((k) => {
            const d = def(k);
            return (
              <div key={k} className="tp-q">
                <div><b>{d.label}</b>{d.help && <small>{d.help}</small>}</div>
                <div>{k === "restore_test_days" ? (
                  <span style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input className="inp" type="date" style={{ maxWidth: 180 }} value={restoreOn} disabled={!edit} aria-label="Last successful restore test"
                      onChange={(e) => { setRestoreOn(e.target.value); const n = e.target.value ? Math.max(0, Math.round((Date.parse(`${todayEastern()}T12:00:00Z`) - Date.parse(`${e.target.value}T12:00:00Z`)) / 864e5)) : null; setVals({ ...vals, [k]: n === null ? "" : String(n) }); }} />
                    <span className="cap">{vals[k] !== "" ? `${vals[k]} days ago` : "Pick the date the restore worked"}</span>
                  </span>
                ) : d.kind === "bool" ? (
                  <span className="tp-seg">{[["yes", d.yes ?? "Yes"], ["no", d.no ?? "No"]].map(([v, l]) => <button key={v} className={vals[k] === v ? "on" : ""} disabled={!edit} onClick={() => setVals({ ...vals, [k]: vals[k] === v ? "" : v! })}>{l}</button>)}</span>
                ) : d.kind === "enum" ? (
                  <select className="inp" value={vals[k]} disabled={!edit} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} style={{ maxWidth: 260 }}><option value="">Not answered</option>{d.options!.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                ) : (
                  <input className="inp" inputMode="decimal" style={{ maxWidth: 200 }} value={vals[k]} disabled={!edit} placeholder="Not answered" onChange={(e) => setVals({ ...vals, [k]: e.target.value })} />
                )}</div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
function toIn(d: RhMetricDef, v: unknown) { if (v === null || v === undefined) return ""; if (d.kind === "bool") return v ? "yes" : "no"; return String(v); }
